import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import CommunicationsHubModal from './CommunicationsHubModal';
import MarketingHubModal from './MarketingHubModal';
import FundRaisingHubModal from './FundRaisingHubModal';
import LegalComplianceHubModal from './LegalComplianceHubModal';
import ResearchHubModal from './research/ResearchHubModal';
import OllamaLabHubModal from './OllamaLabHubModal';

const OPOAgentsTab = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [communicationsHubModalOpen, setCommunicationsHubModalOpen] = useState(false);
  const [marketingHubModalOpen, setMarketingHubModalOpen] = useState(false);
  const [fundRaisingHubModalOpen, setFundRaisingHubModalOpen] = useState(false);
  const [legalComplianceHubModalOpen, setLegalComplianceHubModalOpen] = useState(false);
  const [researchHubModalOpen, setResearchHubModalOpen] = useState(false);
  const [aiLabModalOpen, setAiLabModalOpen] = useState(false);
  const [aiLabOverview, setAiLabOverview] = useState(null);

  const openAuditor = () => navigate('/opo/auditor');

  const isOptiprimeHubPill = (label) => label === 'Auditor' || label === 'Communications' || label === 'Marketing' || label === 'Fund Raising' || label === 'Legal & Compliance' || label === 'Research';

  useEffect(() => {
    const fetchData = async () => {
      try {
        setError(null);
        const res = await fetch('/api/opo/agents');
        if (res.ok) setData(await res.json());
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetch('/api/opo/ai-lab/overview')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && !d.error && setAiLabOverview(d))
      .catch(() => {});
  }, []);

  const getStatusColor = (status) => {
    if (!status) return '#6b7280';
    const s = String(status).toLowerCase();
    if (s === 'up' || s === 'connected') return '#10b981';
    if (s === 'no_key') return '#6b7280';
    return '#ef4444';
  };

  if (loading && !data) {
    return (
      <div className="opo-loading-container">
        <div className="opo-loading-spinner" />
        <p>Loading AI agents...</p>
      </div>
    );
  }
  if (error) return <div className="opo-error">Error: {error}</div>;
  if (!data) return null;

  const { openai, ollama, anthropic, embeddings, usage, lastUpdate, locations } = data;

  // OptiPrime AI Inc departments — 12 sections (00_executive_team → 11_communications) + 13th: Fund Raising
  // Each pill represents a department agent (or planned agent). Auditor, Communications, Marketing, Fund Raising are built.
  const optiPrimeAgentLabels = [
    { label: 'Executive Team', built: false },
    { label: 'Auditor', built: true },
    { label: 'Communications', built: true },
    { label: 'Legal & Compliance', built: true },
    { label: 'Technical Operations', built: false },
    { label: 'Product Managers', built: false },
    { label: 'Sales', built: false },
    { label: 'Marketing', built: true },
    { label: 'Retention & Services', built: false },
    { label: 'HR & Admin', built: false },
    { label: 'Investor Relations', built: false },
    { label: 'Board & Corporate Records', built: false },
    { label: 'Fund Raising', built: true },
  ];

  return (
    <div className="opo-agents-tab">
      <div className="opo-agents-header">
        <span className="opo-last-update">Last updated: {lastUpdate ? new Date(lastUpdate).toLocaleString() : '—'}</span>
      </div>

      <div className="opo-agents-section opo-optiprime-agents-section">
        <h2>OptiPrime AI Inc. Agents</h2>
        <p className="opo-section-desc">Planned agents by department (OptiPrime AI Inc).</p>
        <div className="opo-optiprime-pills-grid">
          {optiPrimeAgentLabels.map(({ label, built }) => (
            <span
              key={label}
              className={`opo-optiprime-pill${built ? ' opo-optiprime-pill-built' : ' opo-optiprime-pill-planned'}${isOptiprimeHubPill(label) ? ' opo-optiprime-pill-clickable' : ''}`}
              role={isOptiprimeHubPill(label) ? 'button' : undefined}
              tabIndex={isOptiprimeHubPill(label) ? 0 : undefined}
              onClick={
                label === 'Auditor'
                  ? openAuditor
                  : label === 'Communications'
                    ? () => setCommunicationsHubModalOpen(true)
                    : label === 'Marketing'
                      ? () => setMarketingHubModalOpen(true)
                      : label === 'Fund Raising'
                        ? () => setFundRaisingHubModalOpen(true)
                        : label === 'Legal & Compliance'
                          ? () => setLegalComplianceHubModalOpen(true)
                          : label === 'Research'
                            ? () => setResearchHubModalOpen(true)
                            : undefined
              }
              onKeyDown={
                label === 'Auditor'
                  ? (e) => (e.key === 'Enter' || e.key === ' ') && openAuditor()
                  : label === 'Communications'
                    ? (e) => (e.key === 'Enter' || e.key === ' ') && setCommunicationsHubModalOpen(true)
                    : label === 'Marketing'
                      ? (e) => (e.key === 'Enter' || e.key === ' ') && setMarketingHubModalOpen(true)
                      : label === 'Fund Raising'
                        ? (e) => (e.key === 'Enter' || e.key === ' ') && setFundRaisingHubModalOpen(true)
                        : label === 'Legal & Compliance'
                          ? (e) => (e.key === 'Enter' || e.key === ' ') && setLegalComplianceHubModalOpen(true)
                          : label === 'Research'
                            ? (e) => (e.key === 'Enter' || e.key === ' ') && setResearchHubModalOpen(true)
                            : undefined
              }
              title={built ? 'Agent built' : 'Agent planned'}
            >
              {label}
              <span className="opo-optiprime-pill-status">{built ? ' ●' : ' ○'}</span>
            </span>
          ))}
        </div>
      </div>

      <div className="opo-agents-section opo-locations-section">
        <h2>Agents by location</h2>
        <p className="opo-section-desc">Every agent running or in development across OptiPrime locations.</p>
        {(locations || []).map((loc) => (
          <div key={loc.id} className="opo-location-card">
            <div className="opo-location-header">
              <h3>{loc.name}</h3>
              {loc.description && <span className="opo-location-desc">{loc.description}</span>}
            </div>
            <div className="opo-location-agents">
              {(loc.agents || []).length === 0 ? (
                <p className="opo-no-agents">No agents found</p>
              ) : (
                <ul className="opo-location-agent-list">
                  {(loc.agents || []).map((agent, i) => {
                    const isAuditor = loc.id === 'optiprime_ai_inc' && (agent.id === '01_auditor/agents/auditor' || (agent.name && agent.name.toLowerCase() === 'auditor'));
                    const isCommunications =
                      loc.id === 'optiprime_ai_inc' &&
                      (agent.id === 'communications' ||
                        (agent.name && String(agent.name).toLowerCase().includes('communications')));
                    const isMarketing =
                      loc.id === 'optiprime_ai_inc' &&
                      (agent.id === 'marketing' ||
                        (agent.name && String(agent.name).toLowerCase().includes('marketing')));
                    const isFundRaising =
                      loc.id === 'optiprime_ai_inc' &&
                      (agent.id === '12_fundraising/agents/coordinator' ||
                        (agent.name && String(agent.name).toLowerCase().includes('fund raising')));
                    const isLegalCompliance =
                      loc.id === 'optiprime_ai_inc' &&
                      (agent.id === 'legal-assistant' ||
                        agent.id === '02_legal_compliance/agents/ministry' ||
                        (agent.name && String(agent.name).toLowerCase().includes('legal')));
                    const hubClick = isAuditor
                      ? openAuditor
                      : isCommunications
                        ? () => setCommunicationsHubModalOpen(true)
                        : isMarketing
                          ? () => setMarketingHubModalOpen(true)
                          : isFundRaising
                            ? () => setFundRaisingHubModalOpen(true)
                            : isLegalCompliance
                              ? () => setLegalComplianceHubModalOpen(true)
                              : undefined;
                    const hubClickable = isAuditor || isCommunications || isMarketing || isFundRaising || isLegalCompliance;
                    return (
                    <li
                      key={agent.id || agent.name || i}
                      className={`opo-location-agent-item${hubClickable ? ' opo-location-agent-item-clickable' : ''}`}
                      style={hubClickable ? { cursor: 'pointer' } : undefined}
                      onClick={hubClick}
                      role={hubClickable ? 'button' : undefined}
                    >
                      <strong>{agent.name}</strong>
                      {agent.type && <span className="opo-agent-type">{agent.type}</span>}
                      {agent.section && <span className="opo-agent-section">{agent.section}</span>}
                      {agent.status && <span className="opo-agent-status">{agent.status}</span>}
                      {agent.services && agent.services.length > 0 && <span className="opo-agent-services">{agent.services.join(', ')}</span>}
                      {agent.purpose && <span className="opo-agent-purpose">{agent.purpose}</span>}
                      {agent.path && loc.id === 'optiprime_ai_inc' && <span className="opo-agent-path">{agent.path}</span>}
                    </li>
                  );
                  })}
                </ul>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="opo-agents-section">
        <h2>OpenAI Assistants</h2>
        <span className="opo-provider-status" style={{ backgroundColor: getStatusColor(openai?.status) }}>
          API: {openai?.status ?? '—'}
        </span>
        <div className="opo-agents-grid">
          {(openai?.assistants || []).map((a, i) => (
            <div key={a.id || i} className="opo-agent-card">
              <div className="opo-agent-card-header">
                <strong>{a.name}</strong>
              </div>
              <div className="opo-agent-details">
                <div className="opo-detail-row"><span>ID:</span><span className="opo-mono">{a.id}</span></div>
                <div className="opo-detail-row"><span>Services:</span><span>{a.services?.join(', ') || '—'}</span></div>
                {a.name && a.name.includes('Legal') && <div className="opo-detail-row"><span>Queries (30d):</span><span>{usage?.legal_assistant ?? 0}</span></div>}
                {a.name && a.name.includes('Operator') && <div className="opo-detail-row"><span>Queries (30d):</span><span>{usage?.normal_assistant ?? 0}</span></div>}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="opo-agents-section">
        <h2>Ollama (local LLMs)</h2>
        <span className="opo-provider-status" style={{ backgroundColor: getStatusColor(ollama?.status) }}>
          Ollama: {ollama?.status ?? '—'}
        </span>
        <div className="opo-agents-grid">
          {(ollama?.known || []).map((m, i) => (
            <div key={m.name || i} className="opo-agent-card">
              <div className="opo-agent-card-header"><strong>{m.name}</strong></div>
              <div className="opo-agent-details">
                <div className="opo-detail-row"><span>Purpose:</span><span>{m.purpose || '—'}</span></div>
                <div className="opo-detail-row"><span>Loaded:</span><span>{(ollama?.models || []).some(x => (x.name || '').startsWith(m.name?.split(':')[0])) ? 'Yes' : '—'}</span></div>
              </div>
            </div>
          ))}
        </div>
        <div className="opo-ai-lab-launcher" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
          <button
            type="button"
            className="opo-optiprime-pill opo-optiprime-pill-built opo-optiprime-pill-clickable"
            style={{ cursor: 'pointer', border: 'none', font: 'inherit' }}
            onClick={() => setAiLabModalOpen(true)}
          >
            🧪 Open AI Lab
          </button>
          <span className="opo-agent-type">{aiLabOverview ? `${aiLabOverview.installed} installed` : `${ollama?.models?.length ?? 0} installed`}</span>
          {aiLabOverview && (
            <>
              <span className="opo-agent-type" style={{ color: '#10b981' }}>{aiLabOverview.tested} tested</span>
              <span className="opo-agent-type" style={{ color: '#f59e0b' }}>{aiLabOverview.untested} untested</span>
            </>
          )}
        </div>
      </div>

      <div className="opo-agents-section">
        <h2>Anthropic</h2>
        <span className="opo-provider-status" style={{ backgroundColor: getStatusColor(anthropic?.status) }}>
          API: {anthropic?.status ?? '—'}
        </span>
        <div className="opo-agents-grid">
          {(anthropic?.models || []).map((m, i) => (
            <div key={m.name || i} className="opo-agent-card">
              <div className="opo-agent-card-header"><strong>{m.name}</strong></div>
              <div className="opo-agent-details">
                <div className="opo-detail-row"><span>Purpose:</span><span>{m.purpose || '—'}</span></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="opo-agents-section">
        <h2>Embeddings</h2>
        <div className="opo-agents-grid">
          {(embeddings || []).map((e, i) => (
            <div key={e.name || i} className="opo-agent-card">
              <div className="opo-agent-card-header"><strong>{e.name}</strong></div>
              <div className="opo-agent-details">
                <div className="opo-detail-row"><span>Provider:</span><span>{e.provider || '—'}</span></div>
                <div className="opo-detail-row"><span>Purpose:</span><span>{e.purpose || '—'}</span></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <CommunicationsHubModal isOpen={communicationsHubModalOpen} onClose={() => setCommunicationsHubModalOpen(false)} />
      <MarketingHubModal isOpen={marketingHubModalOpen} onClose={() => setMarketingHubModalOpen(false)} />
      <FundRaisingHubModal isOpen={fundRaisingHubModalOpen} onClose={() => setFundRaisingHubModalOpen(false)} />
      <LegalComplianceHubModal isOpen={legalComplianceHubModalOpen} onClose={() => setLegalComplianceHubModalOpen(false)} />
      <ResearchHubModal isOpen={researchHubModalOpen} onClose={() => setResearchHubModalOpen(false)} />
      <OllamaLabHubModal isOpen={aiLabModalOpen} onClose={() => setAiLabModalOpen(false)} />

      <div className="opo-agents-section">
        <h2>Usage summary (30 days)</h2>
        <div className="opo-usage-grid">
          <div className="opo-stat-card"><span>Legal assistant:</span><strong>{usage?.legal_assistant ?? 0}</strong></div>
          <div className="opo-stat-card"><span>Normal (OPO) assistant:</span><strong>{usage?.normal_assistant ?? 0}</strong></div>
          <div className="opo-stat-card"><span>OPO chat sessions:</span><strong>{usage?.opo_chat ?? 0}</strong></div>
          <div className="opo-stat-card"><span>MCP assistant messages:</span><strong>{usage?.assistant_messages ?? 0}</strong></div>
        </div>
      </div>
    </div>
  );
};

export default OPOAgentsTab;
